import React, { ReactElement } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import {
  Box,
  Button,
  Card,
  InternalIcon,
  Link,
  Stack,
  Tile,
  Typography,
  useTheme
} from "@carrotfertility/carotene-core"
import { DbgPaths } from "../../../utils/Paths"
import { useBenefitHighlights, ValueProposition } from "./hooks/useBenefitHighlights"
import { useFlags } from "launchdarkly-react-client-sdk"
import { ProgramType } from "../../carrot-plans/Benefit"

const LaunchDarklyBenefitHighlightMvpContainer = ({ children }: { children: ReactElement }): ReactElement => {
  const { benefitHighlightMvp } = useFlags()

  if (!benefitHighlightMvp) {
    return null
  }

  return children
}

function BenefitHighlightsTile(): ReactElement {
  const theme = useTheme()
  const { isBenefitLoading, programTypeAllowsBenefitHighlight, filteredValueProps } = useBenefitHighlights()

  if (isBenefitLoading || !programTypeAllowsBenefitHighlight) {
    return null
  }

  return (
    <Tile id="benefit-highlights-tile" color="info">
      <Stack spacing={theme.spacing(theme.tokens.spacing.md)} paddingBottom={theme.spacing(theme.tokens.spacing.md)}>
        <Typography variant="h3">
          <FormattedMessage defaultMessage="Benefit highlights" />
        </Typography>
        <BenefitHighlightsInner filteredValueProps={filteredValueProps} />
      </Stack>
    </Tile>
  )
}
function BenefitHighlightsCard(): ReactElement {
  const intl = useIntl()
  const { isBenefitLoading, programTypeAllowsBenefitHighlight, filteredValueProps, programType } =
    useBenefitHighlights()

  if (isBenefitLoading || !programTypeAllowsBenefitHighlight) {
    return null
  }

  return (
    <Card
      id="benefit-highlights-sidebar"
      color="info"
      title={intl.formatMessage({
        defaultMessage: "Benefit highlights"
      })}
      primaryAction={
        programType === ProgramType.PRO && (
          <Button variant="outlined" color="secondary" href={DbgPaths.CARROT_FUNDS} component="a">
            <FormattedMessage defaultMessage="Learn to use Carrot funds" />
          </Button>
        )
      }
    >
      <BenefitHighlightsInner useCompactSpacing filteredValueProps={filteredValueProps} />
    </Card>
  )
}

function BenefitHighlightsInner({ useCompactSpacing, filteredValueProps }: BenefitHighlightsProps): ReactElement {
  const theme = useTheme()

  return (
    <Stack spacing={theme.spacing(theme.tokens.spacing.lg)} paddingTop={theme.spacing(theme.tokens.spacing.md)}>
      {filteredValueProps.map((valueProp: ValueProposition) => (
        <ValuePropDisplay
          valueProp={valueProp}
          stackSpacing={theme.spacing(useCompactSpacing ? theme.tokens.spacing.md : theme.tokens.spacing.lg)}
          key={valueProp.valuePropId}
        />
      ))}
    </Stack>
  )
}

function ValuePropDisplay({ valueProp, stackSpacing }: ValuePropDisplayProps): ReactElement {
  const theme = useTheme()
  const intl = useIntl()
  return (
    <Stack direction="row" alignItems={"center"} spacing={stackSpacing}>
      <Box
        component="img"
        src={`/images/${valueProp.iconId}.png`}
        srcSet={`/images/${valueProp.iconId}-2x.png`}
        alt=""
        height={36}
        width={36}
        margin={theme.spacing(theme.tokens.spacing.sm)}
      />
      <Link
        href={valueProp.link}
        color={theme.palette.text.primary}
        endIcon={<InternalIcon fontSize="small" color="secondary" />}
      >
        {intl.formatMessage(valueProp.valuePropMessage)}
      </Link>
    </Stack>
  )
}

interface BenefitHighlightsProps {
  useCompactSpacing?: boolean
  filteredValueProps: ValueProposition[]
}

interface ValuePropDisplayProps {
  valueProp: ValueProposition
  stackSpacing: string
  key: any
}
export { LaunchDarklyBenefitHighlightMvpContainer, BenefitHighlightsTile, BenefitHighlightsCard }
